import { usePageMeta } from "@/lib/seo";
import { useEffect, useRef, useState } from "react";
import { Camera, Check, Copy, Heart, ImagePlus, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { useFavorite } from "@/components/dashboard/ReplyCard";
import { api, errMsg, newRequestId } from "@/lib/api";
import { QuotaBlock } from "@/components/UsagePill";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelect } from "@/components/dashboard/StyleControls";

const COUNTS = [
  { id: 5, name: "5 openers", desc: "Mixed angles" },
  { id: 4, name: "4 styles", desc: "Smooth · Funny · Cute · Bold" },
  { id: 3, name: "3 openers", desc: "Fewer, sharper" },
];
const MAX_MB = 10;

const Chip = ({ active, onClick, testId, children }) => (
  <button data-testid={testId} onClick={onClick}
    className={`px-4 py-2 rounded-2xl text-left border transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
    {children}
  </button>
);

const OpenerCard = ({ opener, index }) => {
  const n = index + 1;
  const [copied, setCopied] = useState(false);
  const [favId, favBusy, toggleFav] = useFavorite(opener);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(opener.text);
      setCopied(true);
      toast.success("Copied! Now send it.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.message("Clipboard blocked — select the text and copy it manually.");
    }
  };
  return (
    <div data-testid={`opener-card-${n}`} className="panel p-5 rise" style={{ animationDelay: `${index * 0.07}s` }}>
      <div className="flex items-center justify-between mb-2">
        <span data-testid={`opener-label-${n}`} className="text-[11px] uppercase tracking-widest font-bold text-accent">{opener.label}</span>
        <button data-testid={`opener-favorite-button-${n}`} onClick={toggleFav} disabled={favBusy} aria-pressed={!!favId} aria-label="Save to favourites"
          className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-secondary active:scale-90 transition-transform">
          <Heart className={`h-4 w-4 ${favId ? "fill-accent text-accent" : "text-muted-foreground"}`} />
        </button>
      </div>
      <p data-testid={`opener-text-${n}`} translate="no" className="notranslate text-base sm:text-lg leading-snug font-medium whitespace-pre-wrap select-text">{opener.text}</p>
      {opener.why && <p className="mt-2 text-xs text-muted-foreground">{opener.why}</p>}
      <button data-testid={`opener-copy-button-${n}`} onClick={copy} className="mt-4 h-9 px-4 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 brand-gradient text-white">
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const PhotoPicker = ({ file, preview, onPick, onClear }) => {
  const ref = useRef(null);
  const pick = (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) return toast.error("Please choose a PNG, JPEG or WebP photo.");
    if (f.size > MAX_MB * 1024 * 1024) return toast.error(`Photo must be under ${MAX_MB} MB.`);
    onPick(f);
  };
  return (
    <div className="panel p-4" data-testid="photo-picker">
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="photo-input"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {file ? (
        <div className="relative">
          <img src={preview} alt="Selected" data-testid="photo-preview" className="w-full max-h-[420px] object-contain rounded-2xl bg-secondary/40" />
          <button data-testid="photo-clear-button" onClick={onClear} aria-label="Remove photo" className="absolute top-2 right-2 h-9 w-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button data-testid="photo-upload-button" onClick={() => ref.current?.click()}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
          className="w-full h-64 rounded-2xl border-2 border-dashed border-border hover:border-primary/60 flex flex-col items-center justify-center gap-3 transition-colors">
          <span className="h-14 w-14 rounded-full brand-gradient text-white flex items-center justify-center"><ImagePlus className="h-6 w-6" /></span>
          <span className="font-semibold">Upload a photo</span>
          <span className="text-xs text-muted-foreground">Profile pic, Insta post or dating app photo · PNG/JPEG/WebP · max {MAX_MB} MB</span>
        </button>
      )}
    </div>
  );
};

export default function PhotoRizz() {
  usePageMeta({ title: "Photo Rizz" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [count, setCount] = useState(5);
  const { onboarding } = useAuth();
  const [language, setLanguage] = useState(onboarding?.profile?.language || "english");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(false);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  const pick = (f) => { setFile(f); setPreview(URL.createObjectURL(f)); setResult(null); setError(""); };
  const clear = () => { setFile(null); setPreview(""); setResult(null); setError(""); };

  const generate = async () => {
    setBusy(true); setError("");
    const form = new FormData();
    form.append("file", file);
    form.append("count", String(count));
    form.append("language", language);
    form.append("context", context.trim());
    form.append("request_id", newRequestId());
    try {
      const { data } = await api.post("/photo-openers", form, { timeout: 90000 });
      setResult(data);
      setQuota(false);
    } catch (e) {
      setQuota(e?.response?.status === 402);
      setError(errMsg(e, "Couldn't create openers. Please retry."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_1.1fr] gap-8" data-testid="photo-rizz-page">
        <section className="space-y-5">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Camera className="h-7 w-7 text-accent" />Photo Rizz</h1>
            <p className="text-sm text-muted-foreground mt-2">Drop their photo — AI reads the vibe and writes flirty openers to start the chat. Photos are never saved.</p>
          </div>
          <PhotoPicker file={file} preview={preview} onPick={pick} onClear={clear} />
          <div className="space-y-2">
            <p className="text-sm font-semibold">How many lines?</p>
            <div className="grid grid-cols-3 gap-2" data-testid="opener-count-options">
              {COUNTS.map((c) => (
                <Chip key={c.id} testId={`opener-count-${c.id}`} active={count === c.id} onClick={() => setCount(c.id)}>
                  <span className="block text-sm font-semibold">{c.name}</span><span className="block text-[11px] text-muted-foreground">{c.desc}</span>
                </Chip>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold">Language</p>
            <LanguageSelect value={language} onChange={setLanguage} testId="opener-language-select" withAuto={false} />
          </div>
          <div className="space-y-2">
            <label htmlFor="photo-context" className="text-sm font-semibold">Any context? <span className="font-normal text-muted-foreground">(optional)</span></label>
            <Input id="photo-context" data-testid="photo-context-input" value={context} maxLength={300} onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. Tinder match, their bio says they love travel" className="rounded-xl" />
          </div>
          <button data-testid="photo-generate-button" onClick={generate} disabled={!file || busy}
            className="w-full h-12 rounded-full brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 hover:opacity-90 transition-opacity">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : result ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Reading the photo…" : result ? "Give me new lines" : "Generate opener lines"}
          </button>
        </section>

        <section className="space-y-4" data-testid="photo-results">
          {busy && [0, 1, 2].map((i) => <div key={i} className="h-28 rounded-3xl shimmer" />)}
          {!busy && error && quota && <QuotaBlock message={error} />}
          {!busy && error && !quota && <div className="panel p-5 border-rose-500/40 text-sm" data-testid="photo-error">{error}</div>}
          {!busy && result && !result.suitable && (
            <div className="panel p-5 text-sm" data-testid="photo-unsuitable">
              We can't write lines for this photo. {result.reason}
            </div>
          )}
          {!busy && result?.suitable && (
            <>
              <div className="panel p-5" data-testid="photo-vibe">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Vibe check</p>
                <p className="mt-1 font-semibold">{result.vibe}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {result.details.map((d, i) => <span key={i} className="text-xs px-3 py-1 rounded-full bg-secondary">{d}</span>)}
                </div>
              </div>
              {result.openers.map((o, i) => <OpenerCard key={`${o.text}-${i}`} opener={o} index={i} />)}
              <p className="text-[11px] text-muted-foreground">AI suggestions — stay respectful and follow their lead.</p>
            </>
          )}
          {!busy && !result && !error && (
            <div className="panel p-8 text-center text-sm text-muted-foreground h-full flex items-center justify-center" data-testid="photo-empty">
              Upload a photo and tap "Generate opener lines" — your lines will appear here.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
